// src/pages/OwnerDashboardPage.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useNotification } from '../contexts/NotificationContext';


import { useTheme } from '../contexts/ThemeContext';
import { mapErrorKey } from '../utils/errorMapping';
import tournamentService from '../services/tournamentService';
import { getDefaultTournamentData } from '../utils/defaultTournamentData';
import { Button } from 'baseui/button';
import { ParagraphMedium, LabelMedium } from 'baseui/typography';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import { Spinner } from 'baseui/spinner';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from 'baseui/modal';
import {
  PageContainer,
  SectionContainer,
  StyledHr,
} from '../styles/controllerPageStyles';
import {
  SettingsIcon,
} from '../styles/iconStyles';
import {
  DashboardContainer,
  TournamentActionButton,
  DashboardHeaderContainer,
  DashboardTitleContainer,
  DashboardHeading,
  DashboardParagraph,
  DashboardActionsContainer,
  ErrorParagraph,
  ButtonGroup,
  DashboardContentContainer,
  DeleteButtonOverrides,
} from '../styles/dashboardPageStyles';
import PageFooter from '../components/common/PageFooter';


const OwnerDashboardPage = () => {
  const { currentUser, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { showNotification } = useNotification();
  const { themeMode, setThemeMode } = useTheme();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const fetchTournaments = useCallback(async () => {
    setIsLoading(true);
    const { data: fetchedTournaments, error } = await tournamentService.getTournamentsForUser(currentUser.id);
    if (error) {
      setError(error.message || t('errors.tournament.fetchFailed'));
    } else if (fetchedTournaments) {
      setTournaments(fetchedTournaments.map(t => ({
        ...t,
        name: t.name,
        createdAt: t.created_at,
        statusKey: determineStatusKey(t.timer_state, t.structure),
      })));
    }
    setIsLoading(false);
  }, [currentUser, t]);

  useEffect(() => {
    if (currentUser) {
      fetchTournaments();
    }
  }, [currentUser, fetchTournaments]);

  const handleCreateTournament = async () => {
    setIsCreating(true);
    setError('');
    try {
      const { name, structure, timer_state, details } = getDefaultTournamentData();

      const { error: createError } = await tournamentService.createTournament(name, structure, timer_state, details);

      if (createError) {
        showNotification(t(mapErrorKey(createError)), 'error');
        throw createError;
      }

      // 新しく作成されたトーナメントのIDを取得してControllerPageに遷移
      // fetchTournaments()でリストを更新し、その中から新しいトーナメントを見つける
      const { data: updatedTournaments, error: fetchError } = await tournamentService.getTournamentsForUser(currentUser.id);
      if (fetchError) {
        throw fetchError;
      }
      const newTournament = updatedTournaments.find(t => t.name === name && t.structure.levels.length === structure.levels.length); // 簡易的な特定方法
      if (newTournament) {
        navigate(`/controller/${newTournament.id}`, { state: { initialData: newTournament } });
      } else {
        // 見つからなかった場合は、通常のダッシュボードにリダイレクト
        await fetchTournaments();
      }
    } catch (error) {
      setError(t(mapErrorKey(error)) || t('errors.tournament.createFailed'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteTournament = async (id) => {
    if (window.confirm(t('tournament.confirmDeleteTournament', { id }))) {
      try {
        const { error: deleteError } = await tournamentService.deleteTournament(id);
        if (deleteError) {
          throw deleteError;
        }
        setTournaments(prev => prev.filter(t => t.id !== id));
      } catch (error) {
        setError(mapErrorKey(error.message) || t('errors.tournament.deleteFailed'));
      }
    }
  }

  function determineStatusKey(timer, structure) {
    switch (timer.state) {
      case 'ready':
        return 'status.ready';
      case 'play':
        const currentLevel = structure?.levels?.[timer.rowIndex];
        if (timer.rowIndex === 0 && timer.hasTournamentStarted === false && currentLevel?.type === 'break') {
          return 'status.countdown';
        }
        return 'status.in_progress';
      case 'pause':
        return 'status.paused';
      case 'stop':
        return 'status.finished';
      default:
        return 'status.unknown';
    }
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const renderContent = () => {
    if (isLoading) {
      return <Spinner />;
    }
    if (tournaments.length === 0) {
      return <ParagraphMedium>{t('pages.dashboard.noTournamentsYet')}</ParagraphMedium>;
    }
    return (
      <DashboardContainer>
        <TableBuilder data={tournaments}>
          <TableBuilderColumn header={t('tournament.table.nameHeader')}>
            {(row) => row.name || t('tournament.untitledTournament', { id: row.id.substring(0, 6) })}
          </TableBuilderColumn>
          <TableBuilderColumn header={t('tournament.table.createdAtHeader')}>
            {(row) => new Date(row.createdAt).toLocaleString()}
          </TableBuilderColumn>
          <TableBuilderColumn header={t('tournament.table.statusHeader')}>
            {(row) => t(row.statusKey)}
          </TableBuilderColumn>
          <TableBuilderColumn header={t('tournament.table.actionsHeader')}>
            {(row) => (
              <>
                <Button size="compact" onClick={() => navigate(`/controller/${row.id}`)}>{t('common.openButton')}</Button>
                <TournamentActionButton
                  size="compact"
                  kind="minimal"
                  onClick={() => handleDeleteTournament(row.id)}
                  overrides={DeleteButtonOverrides}
                >{t('common.deleteButton')}</TournamentActionButton>
              </>
            )}
          </TableBuilderColumn>
        </TableBuilder>
      </DashboardContainer>
    );
  };

  return (
    <PageContainer>
      <DashboardHeaderContainer>
        <DashboardTitleContainer>
          <DashboardHeading>{t('pages.dashboard.title')}</DashboardHeading>
          {currentUser && (
            <DashboardParagraph>{t('pages.dashboard.welcomeMessage', { email: currentUser.email })}</DashboardParagraph>
          )}
        </DashboardTitleContainer>
        <Button onClick={() => setIsSettingsModalOpen(true)} kind="tertiary" shape="circle">
          <SettingsIcon />
        </Button>
      </DashboardHeaderContainer>

      <SectionContainer>
        <DashboardActionsContainer>
          <Button onClick={handleCreateTournament} disabled={isCreating} data-testid="create-tournament-btn">
            {t('pages.dashboard.createNewTournamentButton')}
          </Button>
          {currentUser && currentUser.tournamentLimit && (
            <ParagraphMedium>
              {t('pages.dashboard.currentTournamentsCount', { count: tournaments.length, limit: currentUser.tournamentLimit })}
            </ParagraphMedium>
          )}
        </DashboardActionsContainer>
        {error && <ErrorParagraph>{error}</ErrorParagraph>}
        <StyledHr />
        {renderContent()}
        <StyledHr />
        <ButtonGroup>
          <Button onClick={() => navigate('/manual')} kind="secondary">{t('common.manual')}</Button>
          {currentUser?.role === 'admin' && (
            <Button onClick={() => navigate('/admin')} kind="secondary">{t('pages.dashboard.goToAdminPage')}</Button>
          )}
          <Button onClick={handleLogout} kind="secondary">{t('common.logoutButton')}</Button>
        </ButtonGroup>
      </SectionContainer>

      <Modal
        onClose={() => setIsSettingsModalOpen(false)}
        isOpen={isSettingsModalOpen}
        animate
        autoFocus
      >
        <ModalHeader>{t('common.settings')}</ModalHeader>
        <ModalBody>
          <DashboardContentContainer>
            <div>
              <LabelMedium>{t('common.language')}</LabelMedium>
              <ButtonGroup mode="radio" size="compact" shape="pill" selected={locale === 'ja' ? 0 : 1}>
                <Button onClick={() => setLocale('ja')} disabled={locale === 'ja'}>日本語</Button>
                <Button onClick={() => setLocale('en')} disabled={locale === 'en'}>English</Button>
              </ButtonGroup>
            </div>
            <div>
              <LabelMedium>{t('common.theme')}</LabelMedium>
              <ButtonGroup mode="radio" size="compact" shape="pill" selected={themeMode === 'light' ? 0 : 1}>
                <Button onClick={() => setThemeMode('light')} disabled={themeMode === 'light'}>{t('common.lightMode')}</Button>
                <Button onClick={() => setThemeMode('dark')} disabled={themeMode === 'dark'}>{t('common.darkMode')}</Button>
              </ButtonGroup>
            </div>
          </DashboardContentContainer>
        </ModalBody>
        <ModalFooter>
          <ModalButton onClick={() => setIsSettingsModalOpen(false)}>{t('common.close')}</ModalButton>
        </ModalFooter>
      </Modal>
      <PageFooter />
    </PageContainer >
  );
};

export default OwnerDashboardPage;
